<template>
  <div class="presentations-page">
    <div class="page-toolbar">
      <div>
        <p class="page-eyebrow">Catálogo</p>
        <h2>Presentaciones</h2>
      </div>
      <button class="btn btn-dark" type="button" @click="openCreate">
        <i class="bi bi-plus-lg me-1"></i>
        Nueva presentación
      </button>
    </div>

    <section class="rule-panel">
      <div>
        <strong>Stock en unidad operativa</strong>
        <p>La presentación define cómo se compra o vende. El stock real sigue estando en los productos componentes.</p>
      </div>
      <div class="rule-tags">
        <span>Conversión</span>
        <span>Combo</span>
        <span>Precio propio</span>
      </div>
    </section>

    <div class="filters-row">
      <div class="input-group search-box">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input
          v-model.trim="filters.search"
          class="form-control"
          type="search"
          placeholder="Buscar por nombre, SKU o barcode"
          @keyup.enter="fetchPresentations"
        />
      </div>
      <label class="form-check form-switch mb-0">
        <input v-model="filters.includeInactive" class="form-check-input" type="checkbox" @change="fetchPresentations" />
        <span class="form-check-label">Incluir inactivas</span>
      </label>
      <button class="btn btn-outline-secondary" type="button" @click="fetchPresentations">
        <i class="bi bi-arrow-clockwise"></i>
      </button>
    </div>

    <div class="card table-card d-none d-lg-block">
      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead>
            <tr>
              <th>Presentación</th>
              <th>Tipo</th>
              <th>Códigos</th>
              <th>Uso</th>
              <th>Componentes</th>
              <th>Precio</th>
              <th style="width: 190px;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-4">Cargando presentaciones...</td>
            </tr>
            <tr v-else-if="error">
              <td colspan="7" class="text-danger py-4">{{ error }}</td>
            </tr>
            <tr v-else-if="presentations.length === 0">
              <td colspan="7" class="text-center text-muted py-4">Sin presentaciones</td>
            </tr>
            <tr v-else v-for="presentation in presentations" :key="presentation.presentacion_id">
              <td>
                <div class="fw-semibold">{{ presentation.nombre }}</div>
                <small class="text-muted">{{ presentation.descripcion || 'Sin descripción' }}</small>
              </td>
              <td>
                <span :class="['type-badge', presentation.tipo_presentacion === 'COMBO' ? 'combo' : 'conversion']">
                  {{ presentation.tipo_presentacion }}
                </span>
              </td>
              <td>
                <div>{{ presentation.sku_presentacion || 'Sin SKU' }}</div>
                <small class="text-muted">{{ presentation.codigo_barras || 'Sin barcode' }}</small>
              </td>
              <td>
                <div class="usage-tags">
                  <span v-if="presentation.es_presentacion_compra">Compra</span>
                  <span v-if="presentation.es_presentacion_venta">Venta</span>
                  <span :class="presentation.activa ? 'active' : 'inactive'">
                    {{ presentation.activa ? 'Activa' : 'Inactiva' }}
                  </span>
                </div>
              </td>
              <td>{{ componentsSummary(presentation) }}</td>
              <td>{{ priceSummary(presentation) }}</td>
              <td>
                <button class="btn btn-outline-secondary btn-sm me-2" type="button" @click="openEdit(presentation)">
                  Editar
                </button>
                <button
                  :class="['btn btn-sm', presentation.activa ? 'btn-outline-danger' : 'btn-outline-success']"
                  type="button"
                  @click="toggleActive(presentation)"
                >
                  {{ presentation.activa ? 'Inactivar' : 'Activar' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="mobile-list d-lg-none">
      <div v-if="loading" class="mobile-empty">Cargando presentaciones...</div>
      <div v-else-if="error" class="mobile-empty error">{{ error }}</div>
      <div v-else-if="presentations.length === 0" class="mobile-empty">Sin presentaciones</div>
      <article v-else v-for="presentation in presentations" :key="`m-${presentation.presentacion_id}`" class="presentation-card">
        <div class="card-top">
          <div>
            <span :class="['type-badge', presentation.tipo_presentacion === 'COMBO' ? 'combo' : 'conversion']">
              {{ presentation.tipo_presentacion }}
            </span>
            <h3>{{ presentation.nombre }}</h3>
          </div>
          <span :class="['state-pill', presentation.activa ? 'active' : 'inactive']">
            {{ presentation.activa ? 'Activa' : 'Inactiva' }}
          </span>
        </div>
        <p>{{ presentation.descripcion || 'Sin descripción' }}</p>
        <dl>
          <div>
            <dt>Código</dt>
            <dd>{{ presentation.sku_presentacion || presentation.codigo_barras || 'Sin código' }}</dd>
          </div>
          <div>
            <dt>Componentes</dt>
            <dd>{{ componentsSummary(presentation) }}</dd>
          </div>
          <div>
            <dt>Precio</dt>
            <dd>{{ priceSummary(presentation) }}</dd>
          </div>
        </dl>
        <div class="card-actions">
          <button class="btn btn-outline-secondary btn-sm" type="button" @click="openEdit(presentation)">Editar</button>
          <button
            :class="['btn btn-sm', presentation.activa ? 'btn-outline-danger' : 'btn-outline-success']"
            type="button"
            @click="toggleActive(presentation)"
          >
            {{ presentation.activa ? 'Inactivar' : 'Activar' }}
          </button>
        </div>
      </article>
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>
    <div v-if="showModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable modal-fullscreen-lg-down">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditing ? 'Editar presentación' : 'Nueva presentación' }}</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="formError" class="alert alert-danger">{{ formError }}</div>

            <div class="form-grid">
              <div class="form-field span-2">
                <label class="form-label">Nombre</label>
                <input v-model.trim="form.nombre" class="form-control" type="text" placeholder="Caja x 2 tiras" />
              </div>
              <div class="form-field">
                <label class="form-label">Tipo</label>
                <select v-model="form.tipo_presentacion" class="form-select" @change="handleTypeChange">
                  <option value="CONVERSION">Conversión</option>
                  <option value="COMBO">Combo</option>
                </select>
              </div>
              <div class="form-field">
                <label class="form-label">SKU presentación</label>
                <input v-model.trim="form.sku_presentacion" class="form-control" type="text" placeholder="PRES-001" />
              </div>
              <div class="form-field">
                <label class="form-label">Barcode</label>
                <input v-model.trim="form.codigo_barras" class="form-control" type="text" placeholder="Opcional" />
              </div>
              <div class="form-field span-2">
                <label class="form-label">Descripción</label>
                <textarea v-model.trim="form.descripcion" class="form-control" rows="2" placeholder="Detalle operativo"></textarea>
              </div>
            </div>

            <div class="switch-row">
              <label class="form-check form-switch">
                <input v-model="form.es_presentacion_compra" class="form-check-input" type="checkbox" />
                <span class="form-check-label">Compra/recepción</span>
              </label>
              <label class="form-check form-switch">
                <input v-model="form.es_presentacion_venta" class="form-check-input" type="checkbox" />
                <span class="form-check-label">Venta</span>
              </label>
              <label class="form-check form-switch">
                <input v-model="form.precio_propio_habilitado" class="form-check-input" type="checkbox" />
                <span class="form-check-label">Precio propio</span>
              </label>
              <label class="form-check form-switch">
                <input v-model="form.activa" class="form-check-input" type="checkbox" />
                <span class="form-check-label">Activa</span>
              </label>
            </div>

            <section class="editor-section">
              <div class="section-title">
                <div>
                  <h6>Componentes</h6>
                  <p>Productos reales que ingresan o se descuentan de stock.</p>
                </div>
              </div>

              <div class="component-picker">
                <div class="position-relative flex-grow-1">
                  <input
                    v-model.trim="productSearch"
                    class="form-control"
                    type="search"
                    placeholder="Buscar producto por nombre, SKU o barcode"
                    @input="searchProducts"
                    @focus="showProductResults = productResults.length > 0"
                  />
                  <div v-if="showProductResults" class="product-results">
                    <button
                      v-for="product in productResults"
                      :key="product.producto_id"
                      type="button"
                      class="product-option"
                      @mousedown.prevent="addComponent(product)"
                    >
                      <span>{{ product.nombre }}</span>
                      <small>{{ product.codigo_producto || product.codigo_barras || 'Sin código' }} · {{ product.unidad_medida_nombre || 'unidad' }}</small>
                    </button>
                  </div>
                </div>
              </div>

              <div class="component-list">
                <div v-if="form.componentes.length === 0" class="empty-components">
                  Agregá al menos un producto componente.
                </div>
                <div v-for="(component, index) in form.componentes" :key="component.local_id" class="component-row">
                  <div>
                    <strong>{{ component.producto_nombre }}</strong>
                    <small>{{ component.codigo_producto || 'Sin código' }} · {{ component.unidad_medida_nombre || 'unidad' }}</small>
                  </div>
                  <div class="quantity-field">
                    <label>Cantidad operativa</label>
                    <input
                      v-model.number="component.cantidad_operativa"
                      class="form-control"
                      type="number"
                      min="0.001"
                      step="0.001"
                    />
                  </div>
                  <button class="btn btn-outline-danger btn-sm" type="button" @click="removeComponent(index)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </section>

            <section v-if="showPriceEditor" class="editor-section">
              <div class="section-title">
                <div>
                  <h6>Precio propio</h6>
                  <p>Precio de venta de la presentación o combo.</p>
                </div>
              </div>
              <div class="price-grid">
                <div>
                  <label class="form-label">Precio venta</label>
                  <MoneyInput v-model="form.precio.precio_venta" placeholder="0" />
                </div>
                <div>
                  <label class="form-label">Moneda</label>
                  <input v-model.trim="form.precio.moneda" class="form-control" type="text" />
                </div>
                <div>
                  <label class="form-label">Cantidad desde</label>
                  <input v-model.number="form.precio.cantidad_desde" class="form-control" type="number" min="1" step="1" />
                </div>
                <div>
                  <label class="form-label">Vigencia desde</label>
                  <input v-model="form.precio.vigencia_desde" class="form-control" type="date" />
                </div>
              </div>
            </section>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" type="button" @click="closeModal">Cancelar</button>
            <button class="btn btn-primary" type="button" :disabled="saving || !isFormValid" @click="save">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ isEditing ? 'Guardar cambios' : 'Crear presentación' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/apiService';
import MoneyInput from '@/components/MoneyInput.vue';
import productPresentationsService from '@/services/productPresentationsService';
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs';

const today = () => fechaIsoLocal();

const emptyForm = () => ({
  presentacion_id: null,
  nombre: '',
  descripcion: '',
  tipo_presentacion: 'CONVERSION',
  sku_presentacion: '',
  codigo_barras: '',
  es_presentacion_compra: true,
  es_presentacion_venta: false,
  precio_propio_habilitado: false,
  activa: true,
  componentes: [],
  precio: {
    precio_venta: null,
    moneda: 'PYG',
    cantidad_desde: 1,
    vigencia_desde: today(),
    vigencia_hasta: null
  }
});

export default {
  name: 'PresentacionesView',
  components: { MoneyInput },
  data() {
    return {
      presentations: [],
      loading: false,
      saving: false,
      error: '',
      formError: '',
      showModal: false,
      isEditing: false,
      filters: {
        search: '',
        includeInactive: false
      },
      form: emptyForm(),
      productSearch: '',
      productResults: [],
      productSearchTimer: null,
      showProductResults: false,
      componentLocalSeq: 0
    };
  },
  computed: {
    showPriceEditor() {
      return this.form.precio_propio_habilitado;
    },
    isFormValid() {
      if (!this.form.nombre || this.form.nombre.length < 2) return false;
      if (!this.form.es_presentacion_compra && !this.form.es_presentacion_venta) return false;
      if (!Array.isArray(this.form.componentes) || this.form.componentes.length === 0) return false;
      if (this.form.tipo_presentacion === 'CONVERSION' && this.form.componentes.length !== 1) return false;
      if (this.form.componentes.some((c) => !Number.isFinite(Number(c.cantidad_operativa)) || Number(c.cantidad_operativa) <= 0)) return false;
      if (this.showPriceEditor && (!Number.isFinite(Number(this.form.precio.precio_venta)) || Number(this.form.precio.precio_venta) < 0)) return false;
      return true;
    }
  },
  watch: {
    showModal(value) {
      if (typeof document === 'undefined') return;
      document.body.classList.toggle('modal-open', value);
    }
  },
  mounted() {
    this.fetchPresentations();
  },
  beforeUnmount() {
    if (this.productSearchTimer) clearTimeout(this.productSearchTimer);
    if (typeof document !== 'undefined') document.body.classList.remove('modal-open');
  },
  methods: {
    async fetchPresentations() {
      this.loading = true;
      this.error = '';
      try {
        const data = await productPresentationsService.list({
          search: this.filters.search || '',
          includeInactive: this.filters.includeInactive
        });
        this.presentations = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error(error);
        this.error = 'No se pudieron cargar las presentaciones.';
      } finally {
        this.loading = false;
      }
    },
    componentsSummary(presentation) {
      const components = Array.isArray(presentation.componentes) ? presentation.componentes : [];
      if (!components.length) return 'Sin componentes';
      return components
        .map((c) => `${Number(c.cantidad_operativa)} ${c.unidad_medida_nombre || ''} ${c.producto_nombre || ''}`.trim())
        .join(' + ');
    },
    priceSummary(presentation) {
      if (!presentation.precio_propio_habilitado) return 'Suma componentes';
      const price = Array.isArray(presentation.precios) ? presentation.precios.find((p) => p.activo !== false) : null;
      if (!price) return 'Sin precio vigente';
      return `${Number(price.precio_venta).toLocaleString('es-PY')} ${price.moneda || 'PYG'}`;
    },
    openCreate() {
      this.isEditing = false;
      this.form = emptyForm();
      this.formError = '';
      this.productSearch = '';
      this.productResults = [];
      this.showProductResults = false;
      this.showModal = true;
    },
    async openEdit(presentation) {
      this.isEditing = true;
      this.formError = '';
      this.productSearch = '';
      this.productResults = [];
      this.showProductResults = false;
      try {
        const full = await productPresentationsService.get(presentation.presentacion_id);
        this.form = this.mapPresentationToForm(full || presentation);
        this.showModal = true;
      } catch (error) {
        console.error(error);
        this.error = 'No se pudo abrir la presentación.';
      }
    },
    mapPresentationToForm(presentation) {
      const activePrice = Array.isArray(presentation.precios)
        ? presentation.precios.find((price) => price.activo !== false)
        : null;
      return {
        ...emptyForm(),
        presentacion_id: presentation.presentacion_id,
        nombre: presentation.nombre || '',
        descripcion: presentation.descripcion || '',
        tipo_presentacion: presentation.tipo_presentacion || 'CONVERSION',
        sku_presentacion: presentation.sku_presentacion || '',
        codigo_barras: presentation.codigo_barras || '',
        es_presentacion_compra: Boolean(presentation.es_presentacion_compra),
        es_presentacion_venta: Boolean(presentation.es_presentacion_venta),
        precio_propio_habilitado: Boolean(presentation.precio_propio_habilitado),
        activa: presentation.activa !== false,
        componentes: (presentation.componentes || []).map((component) => ({
          local_id: this.nextLocalComponentId(component.producto_id),
          componente_id: component.componente_id || null,
          producto_id: component.producto_id,
          producto_nombre: component.producto_nombre || '',
          codigo_producto: component.codigo_producto || '',
          cantidad_operativa: Number(component.cantidad_operativa) || 1,
          unidad_medida_id: component.unidad_medida_id,
          unidad_medida_nombre: component.unidad_medida_nombre || ''
        })),
        precio: activePrice
          ? {
            precio_venta: Number(activePrice.precio_venta),
            moneda: activePrice.moneda || 'PYG',
            cantidad_desde: Number(activePrice.cantidad_desde) || 1,
            vigencia_desde: String(activePrice.vigencia_desde || today()).slice(0, 10),
            vigencia_hasta: activePrice.vigencia_hasta || null
          }
          : emptyForm().precio
      };
    },
    closeModal() {
      this.showModal = false;
      this.showProductResults = false;
    },
    handleTypeChange() {
      if (this.form.tipo_presentacion === 'CONVERSION' && this.form.componentes.length > 1) {
        this.form.componentes = this.normalizeComponentsForForm(this.form.componentes).slice(0, 1);
      }
    },
    nextLocalComponentId(productId = 'component') {
      this.componentLocalSeq += 1;
      return `component-${productId}-${this.componentLocalSeq}`;
    },
    createFormComponent(product, overrides = {}) {
      return {
        local_id: this.nextLocalComponentId(product.producto_id),
        componente_id: overrides.componente_id || null,
        producto_id: Number(product.producto_id),
        producto_nombre: product.nombre || product.producto_nombre || '',
        codigo_producto: product.codigo_producto || product.codigo_barras || '',
        cantidad_operativa: Number(overrides.cantidad_operativa ?? 1),
        unidad_medida_id: Number(overrides.unidad_medida_id ?? product.unidad_medida_id),
        unidad_medida_nombre: overrides.unidad_medida_nombre || product.unidad_medida_nombre || '',
        orden: Number(overrides.orden || 0)
      };
    },
    normalizeComponentsForForm(components = []) {
      const seen = new Set();
      return (Array.isArray(components) ? components : [])
        .map((component, index) => {
          const productoId = Number(component.producto_id);
          const unidadMedidaId = Number(component.unidad_medida_id);
          const cantidadOperativa = Number(component.cantidad_operativa);
          if (!Number.isInteger(productoId) || productoId <= 0) return null;
          if (!Number.isInteger(unidadMedidaId) || unidadMedidaId <= 0) return null;
          if (!Number.isFinite(cantidadOperativa) || cantidadOperativa <= 0) return null;
          if (seen.has(productoId)) return null;
          seen.add(productoId);
          return {
            local_id: component.local_id || this.nextLocalComponentId(productoId),
            componente_id: component.componente_id || null,
            producto_id: productoId,
            producto_nombre: component.producto_nombre || '',
            codigo_producto: component.codigo_producto || '',
            cantidad_operativa: cantidadOperativa,
            unidad_medida_id: unidadMedidaId,
            unidad_medida_nombre: component.unidad_medida_nombre || '',
            orden: index + 1
          };
        })
        .filter(Boolean);
    },
    searchProducts() {
      if (this.productSearchTimer) clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(async () => {
        const term = this.productSearch.trim();
        if (term.length < 2) {
          this.productResults = [];
          this.showProductResults = false;
          return;
        }
        try {
          const response = await api.get('/api/products/search', { query: term });
          this.productResults = Array.isArray(response.data) ? response.data : [];
          this.showProductResults = this.productResults.length > 0;
        } catch (error) {
          console.error(error);
          this.productResults = [];
          this.showProductResults = false;
        }
      }, 250);
    },
    addComponent(product) {
      if (this.form.tipo_presentacion === 'CONVERSION' && this.form.componentes.length >= 1) {
        this.formError = 'Una conversión simple usa un solo producto componente.';
        this.showProductResults = false;
        return;
      }
      if (this.form.componentes.some((c) => Number(c.producto_id) === Number(product.producto_id))) {
        this.formError = 'El producto ya está agregado como componente.';
        this.showProductResults = false;
        return;
      }
      this.formError = '';
      this.form.componentes = this.normalizeComponentsForForm([
        ...this.form.componentes,
        this.createFormComponent(product)
      ]);
      this.productSearch = '';
      this.productResults = [];
      this.showProductResults = false;
    },
    removeComponent(index) {
      this.form.componentes.splice(index, 1);
    },
    buildPayload() {
      const normalizedComponents = this.normalizeComponentsForForm(this.form.componentes);
      const payload = {
        nombre: this.form.nombre,
        descripcion: this.form.descripcion || null,
        tipo_presentacion: this.form.tipo_presentacion,
        sku_presentacion: this.form.sku_presentacion || null,
        codigo_barras: this.form.codigo_barras || null,
        es_presentacion_compra: this.form.es_presentacion_compra,
        es_presentacion_venta: this.form.es_presentacion_venta,
        precio_propio_habilitado: this.showPriceEditor,
        activa: this.form.activa,
        componentes: normalizedComponents.map((component, index) => ({
          producto_id: Number(component.producto_id),
          cantidad_operativa: Number(component.cantidad_operativa),
          unidad_medida_id: Number(component.unidad_medida_id),
          orden: index + 1,
          activo: true
        }))
      };

      if (this.showPriceEditor) {
        payload.precios = [{
          precio_venta: Number(this.form.precio.precio_venta),
          moneda: this.form.precio.moneda || 'PYG',
          cantidad_desde: Number(this.form.precio.cantidad_desde) || 1,
          cantidad_hasta: null,
          vigencia_desde: this.form.precio.vigencia_desde || today(),
          vigencia_hasta: this.form.precio.vigencia_hasta || null,
          activo: true
        }];
      } else {
        payload.precios = [];
      }
      return payload;
    },
    async save() {
      if (!this.isFormValid) return;
      this.saving = true;
      this.formError = '';
      try {
        this.form.componentes = this.normalizeComponentsForForm(this.form.componentes);
        const payload = this.buildPayload();
        if (this.form.tipo_presentacion === 'CONVERSION' && payload.componentes.length !== 1) {
          throw new Error('Una presentación de conversión debe guardar exactamente un componente.');
        }
        if (this.form.tipo_presentacion === 'COMBO' && payload.componentes.length < 1) {
          throw new Error('Un combo requiere al menos un componente válido.');
        }
        if (this.isEditing && this.form.presentacion_id) {
          await productPresentationsService.update(this.form.presentacion_id, payload);
        } else {
          await productPresentationsService.create(payload);
        }
        this.showModal = false;
        await this.fetchPresentations();
      } catch (error) {
        console.error(error);
        this.formError = error.userMessage || error.response?.data?.error || error.response?.data?.message || 'No se pudo guardar la presentación.';
      } finally {
        this.saving = false;
      }
    },
    async toggleActive(presentation) {
      try {
        if (presentation.activa) {
          await productPresentationsService.deactivate(presentation.presentacion_id);
        } else {
          await productPresentationsService.activate(presentation.presentacion_id);
        }
        await this.fetchPresentations();
      } catch (error) {
        console.error(error);
        this.error = 'No se pudo cambiar el estado de la presentación.';
      }
    }
  }
};
</script>

<style scoped>
.presentations-page {
  max-width: 1280px;
}

.page-toolbar,
.filters-row,
.switch-row,
.section-title,
.component-picker,
.component-row,
.card-top,
.card-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-toolbar {
  justify-content: space-between;
  margin-bottom: 1rem;
}

.page-eyebrow {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0f766e;
}

.page-toolbar h2 {
  margin: 0;
  font-size: 1.45rem;
}

.rule-panel {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid #dbe3ea;
  border-left: 4px solid #0f766e;
  border-radius: 0.5rem;
  background: #fff;
}

.rule-panel p {
  margin: 0.2rem 0 0;
  color: #4b5563;
}

.rule-tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
}

.rule-tags span,
.usage-tags span,
.state-pill {
  display: inline-flex;
  align-items: center;
  min-height: 1.75rem;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
}

.filters-row {
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.search-box {
  max-width: 460px;
}

.table-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.type-badge {
  display: inline-flex;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
}

.type-badge.conversion {
  background: #ecfdf5;
  color: #047857;
}

.type-badge.combo {
  background: #fff7ed;
  color: #c2410c;
}

.usage-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.usage-tags .active,
.state-pill.active {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.usage-tags .inactive,
.state-pill.inactive {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #d1d5db;
}

.mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.presentation-card,
.mobile-empty {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
  background: #fff;
}

.mobile-empty.error {
  color: #b91c1c;
  border-color: #fecaca;
  background: #fef2f2;
}

.card-top {
  justify-content: space-between;
  align-items: flex-start;
}

.presentation-card h3 {
  margin: 0.35rem 0 0;
  font-size: 1rem;
}

.presentation-card p {
  margin: 0.75rem 0;
  color: #4b5563;
}

.presentation-card dl {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  margin: 0;
}

.presentation-card dt {
  font-size: 0.72rem;
  color: #6b7280;
}

.presentation-card dd {
  margin: 0;
  font-weight: 600;
}

.card-actions {
  margin-top: 0.85rem;
}

.form-grid,
.price-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.span-2 {
  grid-column: span 2;
}

.switch-row {
  flex-wrap: wrap;
  margin: 1rem 0;
}

.editor-section {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f9fafb;
}

.section-title {
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.85rem;
}

.section-title h6 {
  margin: 0;
  font-weight: 800;
}

.section-title p {
  margin: 0.2rem 0 0;
  color: #6b7280;
}

.product-results {
  position: absolute;
  z-index: 1060;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow: auto;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #fff;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.14);
}

.product-option {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 0;
  background: transparent;
  text-align: left;
}

.product-option:hover {
  background: #f3f4f6;
}

.product-option small,
.component-row small {
  color: #6b7280;
}

.component-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.empty-components {
  padding: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.5rem;
  color: #64748b;
  background: #fff;
}

.component-row {
  justify-content: space-between;
  padding: 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fff;
}

.component-row > div:first-child {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}

.quantity-field {
  width: 180px;
}

.quantity-field label {
  display: block;
  margin-bottom: 0.2rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
}

@media (max-width: 991.98px) {
  .page-toolbar,
  .rule-panel,
  .filters-row,
  .component-row,
  .card-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .search-box,
  .quantity-field {
    width: 100%;
    max-width: none;
  }

  .rule-tags {
    justify-content: flex-start;
  }

  .form-grid,
  .price-grid {
    grid-template-columns: 1fr;
  }

  .span-2 {
    grid-column: span 1;
  }
}
</style>
